<template>
  <div class="driver-wrapper">
    <!-- HEADER -->
    <header class="driver-header">
      <div class="driver-header-left">
        <div class="hello">👋 Chào tài xế</div>
        <div class="name">{{ taiXe?.hoTen || 'Tài xế' }}</div>
        <div class="sub">ID: {{ taiXe?.idNguoiDung || '-' }}</div>
      </div>

      <div class="driver-header-mid">
        <div class="stat-box">
          <div class="num">{{ myOrdersCount || 0 }}</div>
          <div class="label">Đang chạy</div>
        </div>
        <div class="stat-box">
          <div class="num">{{ poolCount || 0 }}</div>
          <div class="label">Chờ nhận</div>
        </div>
        <div class="stat-box">
          <div class="num">{{ overtimeTotalDisplay }}</div>
          <div class="label">Tăng ca</div>
        </div>
      </div>

      <button
        class="refresh-btn"
        :disabled="isLoadingList || isRefreshingAll"
        @click="refreshAll"
      >
        <i class="fa fa-refresh" :class="{ 'fa-spin': isLoadingList || isRefreshingAll }"></i>
      </button>
    </header>

    <!-- TABS -->
    <nav class="driver-tabs">
      <button
        class="driver-tab"
        :class="{active: tab==='CUA_TOI'}"
        @click="setTab('CUA_TOI')"
      >
        <div class="tab-main">
          <i class="fa fa-truck mr-1"></i>
          Đơn của tôi
        </div>
        <div class="tab-sub">
          <span v-if="myOrdersCount>0" class="pill pill-primary">{{ myOrdersCount }}</span>
          <span v-else class="muted">Chưa nhận</span>
        </div>
      </button>

      <button
        class="driver-tab"
        :class="{active: tab==='HANG_DOI'}"
        @click="setTab('HANG_DOI')"
      >
        <div class="tab-main">
          <i class="fa fa-inbox mr-1"></i>
          Hàng đợi
        </div>
        <div class="tab-sub">
          <span v-if="poolCount>0" class="pill pill-success">{{ poolCount }}</span>
          <span v-else class="muted">Trống</span>
        </div>
      </button>

      <button
        class="driver-tab"
        :class="{active: tab==='LICH_SU'}"
        @click="setTab('LICH_SU')"
      >
        <div class="tab-main">
          <i class="fa fa-clock-o mr-1"></i>
          Lịch sử
        </div>
        <div class="tab-sub">
          <span class="muted">Tăng ca: {{ overtimeTotalDisplay }}</span>
        </div>
      </button>
    </nav>

    <!-- BODY -->
    <section class="driver-body">

      <!-- LOADING / ERROR -->
      <div v-if="isLoadingList" class="state-block">
        <div class="spinner-border spinner-lg"></div>
        <div class="mt-2 text-muted">Đang tải dữ liệu...</div>
      </div>

      <div v-else-if="listError" class="state-block error">
        <i class="fa fa-exclamation-circle mr-1"></i>
        {{ listError }}
      </div>

      <!-- TAB: CUA_TOI -->
      <template v-else-if="tab==='CUA_TOI'">
        <div v-if="!myOrders.length" class="state-block empty">
          <div class="big-icon text-success"><i class="fa fa-check-circle"></i></div>
          <div class="text-main">Không có đơn đang chạy</div>
          <div class="text-sub">Qua tab "Hàng đợi" để nhận đơn</div>
        </div>

        <div v-else class="order-list">
          <article
            v-for="o in myOrders"
            :key="o.id"
            class="order-item"
            @click="openDetail(o.id)"
          >
            <header class="order-head">
              <div class="order-head-left">
                <div class="order-title text-primary">{{ truncate(o.tenDonHang,60) }}</div>
                <div class="order-badges">
                  <span class="chip" :class="chipStatus(o.trangThai)">
                    {{ humanStatus(o.trangThai) }}
                  </span>
                  <span class="chip chip-outline">
                    {{ humanLoai(o.loaiDonHang) }}
                  </span>
                  <span
                    v-if="o.giaoGap"
                    class="chip chip-urgent"
                  >
                    ⚡ Giao gấp
                  </span>
                </div>
              </div>
              <div class="order-head-right">
                <div class="lbl small text-muted">Hẹn / tạo</div>
                <div class="val">{{ fmtDate(o.thoiGianHen) || fmtDate(o.ngayTao) }}</div>
              </div>
            </header>

            <footer class="order-foot">
              <i class="fa fa-route mr-1 text-muted"></i>
              <span class="small text-muted">Điểm dừng:</span>
              <strong class="small ml-1">
                {{ (o.diemDung && o.diemDung.length) || 1 }} điểm
              </strong>
            </footer>
          </article>
        </div>
      </template>

      <!-- TAB: HANG_DOI -->
      <template v-else-if="tab==='HANG_DOI'">
        <div v-if="!poolOrders.length" class="state-block empty">
          <div class="big-icon"><i class="fa fa-coffee"></i></div>
          <div class="text-main">Không có đơn chờ nhận</div>
          <div class="text-sub">Nghỉ chút nha 😎</div>
        </div>

        <div v-else class="order-list">
          <article
            v-for="o in poolOrders"
            :key="o.id"
            class="order-item"
            @click="openClaim(o)"
          >
            <header class="order-head">
              <div class="order-head-left">
                <div class="order-title text-success">{{ truncate(o.tenDonHang,60) }}</div>
                <div class="order-badges">
                  <span class="chip chip-outline">
                    {{ humanLoai(o.loaiDonHang) }}
                  </span>
                  <span class="chip chip-ghost">
                    {{ (o.diemDung && o.diemDung.length) || 1 }} điểm dừng
                  </span>
                </div>
              </div>
              <div class="order-head-right">
                <div class="lbl small text-muted">Hẹn / tạo</div>
                <div class="val">{{ fmtDate(o.thoiGianHen) || fmtDate(o.ngayTao) }}</div>
              </div>
            </header>

            <footer class="order-foot">
              <i class="fa fa-home mr-1 text-muted"></i>
              <span class="small text-muted">Kho:</span>
              <strong class="small ml-1">{{ o.khoXuat || '—' }}</strong>
            </footer>
          </article>
        </div>
      </template>

      <!-- TAB: LICH_SU -->
      <template v-else>
        <div v-if="!completedOrders.length" class="state-block empty">
          <div class="big-icon text-primary"><i class="fa fa-history"></i></div>
          <div class="text-main">Chưa có đơn hoàn thành</div>
          <div class="text-sub">Chạy xong đơn sẽ hiện ở đây</div>
        </div>

        <div v-else class="history-wrapper">
          <div class="history-total">
            <div class="history-total-left">
              <div class="total-label">Tiền tăng ca (GIAO GẤP):</div>
              <div class="total-val">{{ overtimeTotalDisplay }}</div>
            </div>
            <div class="history-total-right text-right">
              <div class="total-count">
                {{ completedOrders.length }} đơn
              </div>
            </div>
          </div>

          <article
            v-for="h in completedOrders"
            :key="h.id"
            class="history-item"
          >
            <header class="history-head">
              <div class="history-head-left">
                <div class="history-title">
                  {{ h.tenDonHang || ('Đơn #' + h.id) }}
                </div>
                <div class="history-badges">
                  <span class="chip chip-done">Hoàn thành</span>

                  <span
                    v-if="h.troCapGiaoGop && h.troCapGiaoGop>0"
                    class="chip chip-overtime"
                  >
                    +{{ money(h.troCapGiaoGop) }}
                  </span>
                </div>
              </div>
              <div class="history-head-right text-right small">
                <div v-if="h.giaoGap" class="urgent-flag">
                  <i class="fa fa-bolt mr-1"></i> GIAO GẤP
                </div>
                <div v-else class="text-muted">
                  <i class="fa fa-clock-o mr-1"></i> Bình thường
                </div>
              </div>
            </header>

            <div class="history-body small text-muted">
              KH: {{ h.tenKhachHang || '-' }}<br />
              Địa chỉ: {{ h.diaChiGiaoHang || h.diaChiKhachHang || '-' }}<br />
              Xe: {{ h.xeBienSo || '-' }}
            </div>

            <footer class="history-foot small text-muted">
              Tạo lúc: {{ h.ngayTao }}
            </footer>
          </article>
        </div>
      </template>
    </section>

    <!-- ========== BOTTOM SHEETS / MODALS ========== -->

    <!-- DETAIL -->
    <transition name="fade-fast">
      <div v-if="modal.show && modal.type==='DETAIL'" class="sheet-overlay">
        <div class="sheet-card">
          <header class="sheet-header">
            <button class="sheet-close" @click="closeSheet" :disabled="isBusy">
              <i class="fa fa-times"></i>
            </button>

            <div class="sheet-title-row">
              <div class="sheet-title-main">
                <div class="sheet-title-text">
                  {{ currentOrder?.tenDonHang || 'Đang tải...' }}
                </div>
                <div class="sheet-title-badges">
                  <span
                    v-if="currentOrder"
                    class="chip mr-1"
                    :class="chipStatus(currentOrder.trangThai)"
                  >
                    {{ humanStatus(currentOrder.trangThai) }}
                  </span>
                  <span
                    v-if="currentOrder?.loaiDonHang"
                    class="chip chip-outline mr-1"
                  >
                    {{ humanLoai(currentOrder.loaiDonHang) }}
                  </span>
                  <span class="sheet-id">#{{ currentOrder?.id || '—' }}</span>
                </div>
              </div>

              <div class="sheet-title-side">
                <div class="lbl small text-muted">Hẹn giao</div>
                <div class="val">{{ fmtDate(currentOrder?.thoiGianHen) }}</div>
              </div>
            </div>

            <div class="mini-info">
              <div class="mini-row">
                <i class="fa fa-user-circle mini-icon"></i>
                <div class="mini-main">
                  <div class="mini-label">Người nhận</div>
                  <div class="mini-value">
                    {{ currentOrder?.tenNguoiNhan || '—' }}
                  </div>
                </div>
              </div>

              <div class="mini-row">
                <i class="fa fa-phone mini-icon"></i>
                <div class="mini-main">
                  <div class="mini-label">Điện thoại</div>
                  <div class="mini-value">
                    <a
                      class="call-inline"
                      :class="{ disabled: !currentOrder?.sdtNguoiNhan }"
                      :href="currentOrder?.sdtNguoiNhan ? 'tel:'+currentOrder.sdtNguoiNhan : null"
                      @click.stop
                    >
                      {{ currentOrder?.sdtNguoiNhan || '—' }}
                      <i class="fa fa-phone-alt ml-1"></i>
                    </a>
                  </div>
                </div>
              </div>

              <div class="mini-row">
                <i class="fa fa-map-marker mini-icon"></i>
                <div class="mini-main">
                  <div class="mini-label">Giao tại</div>
                  <div class="mini-value">
                    <button
                      class="map-inline"
                      @click.stop="openMap(currentOrder?.diaChiGiaoHang)"
                      :disabled="!currentOrder?.diaChiGiaoHang"
                    >
                      {{ currentOrder?.diaChiGiaoHang || '—' }}
                      <i class="fa fa-external-link-alt ml-1"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="currentOrder?.ghiChuSales || currentOrder?.ghiChuKho" class="notes-wrap">
              <div v-if="currentOrder?.ghiChuSales" class="note-block note-sales">
                <div class="note-head"><i class="fa fa-sticky-note mr-1"></i>Ghi chú Sales</div>
                <div class="note-body">{{ currentOrder.ghiChuSales }}</div>
              </div>

              <div class="note-block note-warehouse">
                <div class="note-head"><i class="fa fa-warehouse mr-1"></i>Ghi chú Kho</div>
                <div class="note-body">{{ currentOrder.ghiChuKho }}</div>
              </div>
            </div>
          </header>

          <section class="sheet-body">
            <div class="section-head">
              <i class="fa fa-road mr-1"></i>
              Lộ trình
              <span class="count">({{ currentOrder?.diemDung?.length || 0 }} điểm dừng)</span>
            </div>

            <div class="stop-list">
              <div
                v-for="stop in currentOrder?.diemDung || []"
                :key="stop.id"
                class="stop-card"
                :class="{
                  done: stop.trangThai==='HOAN_THANH' || stop.trangThai==='KHO_DA_XAC_MINH'
                }"
              >
                <div class="stop-left">
                  <div class="stop-top">
                    <div class="idx">{{ stop.thuTu }}.</div>
                    <div class="title">{{ stop.tenDiemDung }}</div>
                    <div class="stop-chip" :class="stopChip(stop.loaiDiemDung)">
                      {{ stopLoai(stop.loaiDiemDung) }}
                    </div>
                  </div>

                  <div class="stop-addr">
                    <button
                      v-if="stop.diaChi"
                      class="map-inline-link"
                      @click.stop="openMap(stop.diaChi)"
                    >
                      <i class="fa fa-map-marker mr-1"></i>{{ stop.diaChi }}
                    </button>
                    <div v-else class="text-muted small">(Không có địa chỉ)</div>
                  </div>

                  <div v-if="stop.chiTietVatTu" class="stop-mat small text-muted">
                    <i class="fa fa-box mr-1"></i>Vật tư: {{ stop.chiTietVatTu }}
                  </div>

                  <div class="stop-time small text-muted">
                    <div v-if="stop.thoiDiemDen">
                      <i class="fa fa-sign-in mr-1"></i>
                      Đến: {{ fmtDateTime(stop.thoiDiemDen) }}
                    </div>
                    <div v-if="stop.thoiDiemHoanThanh">
                      <i class="fa fa-check mr-1"></i>
                      Xong: {{ fmtDateTime(stop.thoiDiemHoanThanh) }}
                    </div>
                  </div>
                </div>

                <div class="stop-right">
                  <button
                    v-if="canMarkArrivedKho(stop)"
                    class="act-btn act-primary"
                    :disabled="isBusy"
                    @click="checkpoint('den', stop.id)"
                  >
                    <span v-if="isBusy" class="spinner-border spinner-border-sm mr-1"></span>
                    <i v-else class="fa fa-map-marker mr-1"></i>
                    <span>Đã đến kho</span>
                  </button>

                  <button
                    v-if="canMarkArrivedKhach(stop)"
                    class="act-btn act-primary"
                    :disabled="isBusy"
                    @click="checkpoint('den', stop.id)"
                  >
                    <span v-if="isBusy" class="spinner-border spinner-border-sm mr-1"></span>
                    <i v-else class="fa fa-map-marker mr-1"></i>
                    <span>Đã đến</span>
                  </button>

                  <button
                    v-if="canCheckinKhach(stop)"
                    class="act-btn act-warn"
                    :disabled="isBusy"
                    @click="openArrive(stop)"
                  >
                    <span v-if="isBusy" class="spinner-border spinner-border-sm mr-1"></span>
                    <i v-else class="fa fa-camera mr-1"></i>
                    <span>Xác minh</span>
                  </button>

                  <!-- <button
                    v-if="canHoanThanhKhach(stop)"
                    class="act-btn act-ok"
                    :disabled="isBusy"
                    @click="checkpoint('hoan-thanh', stop.id)"
                  >
                    <span v-if="isBusy" class="spinner-border spinner-border-sm mr-1"></span>
                    <i v-else class="fa fa-check mr-1"></i>
                    <span>Đã giao xong</span>
                  </button> -->

                  <div
                    v-if="isChoKhoXacMinh(stop)"
                    class="pending-block"
                  >
                    <i class="fa fa-hourglass-half mr-1"></i>Chờ Kho
                  </div>

                  <div
                    v-if="isStopDone(stop)"
                    class="done-ic text-success"
                  >
                    <i class="fa fa-check-circle"></i>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="currentOrder?.phieuXuatKhoUrl" class="pxk-block">
              <div class="pxk-label">Phiếu Xuất Kho</div>
              <a
                class="pxk-link"
                :href="currentOrder.phieuXuatKhoUrl"
                target="_blank"
                rel="noopener"
              >
                Xem PXK <i class="fa fa-external-link-alt ml-1"></i>
              </a>
            </div>

            <div
              v-if="currentOrder &&
                    currentOrder.trangThai!=='HOAN_THANH' &&
                    currentOrder.trangThai!=='THAT_BAI'"
              class="fail-wrap"
            >
              <button
                class="fail-btn"
                @click="markFailed"
                :disabled="isBusy"
              >
                <i v-if="!isBusy" class="fa fa-times-circle mr-2"></i>
                <span v-else class="spinner-border spinner-border-sm mr-2"></span>
                Báo Thất Bại
              </button>
            </div>
          </section>
        </div>
      </div>
    </transition>

    <!-- CLAIM -->
    <transition name="fade-fast">
      <div v-if="modal.show && modal.type==='CLAIM'" class="sheet-overlay">
        <div class="sheet-card sheet-card-success">
          <header class="sheet-header colored">
            <button class="sheet-close colored" @click="closeSheet" :disabled="isBusy">
              <i class="fa fa-times"></i>
            </button>

            <div class="sheet-title-row">
              <div class="sheet-title-main">
                <div class="sheet-title-text">Nhận đơn này?</div>
                <div class="sheet-claim-name">{{ currentOrder?.tenDonHang || '—' }}</div>
                <div class="sheet-id mt-1">#{{ currentOrder?.id || '—' }}</div>
              </div>
            </div>

            <div class="mini-info colored mt-2">
              <div class="mini-row">
                <i class="fa fa-road mini-icon"></i>
                <div class="mini-main">
                  <div class="mini-label">Lộ trình</div>
                  <div class="mini-value">
                    {{ currentOrder?.diemDung?.length || 0 }} điểm dừng
                  </div>
                </div>
              </div>

              <div class="mini-row" v-if="currentOrder?.phieuXuatKhoUrl">
                <i class="fa fa-warehouse mini-icon"></i>
                <div class="mini-main">
                  <div class="mini-label">PXK</div>
                  <div class="mini-value">
                    <a
                      :href="currentOrder.phieuXuatKhoUrl"
                      target="_blank"
                      rel="noopener"
                      class="pxk-inline"
                    >
                      Xem PXK <i class="fa fa-external-link-alt ml-1"></i>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </header>

          <section class="sheet-body">
            <div class="stop-list-lite">
              <div
                v-for="s in currentOrder?.diemDung || []"
                :key="s.id"
                class="stop-lite"
              >
                <div class="stop-lite-left">
                  <div class="stop-lite-top">
                    <div class="idx">{{ s.thuTu }}.</div>
                    <div class="title">Tên Kho: {{ s.tenDiemDung }}</div>
                    <div class="stop-chip" :class="stopChip(s.loaiDiemDung)">
                      {{ stopLoai(s.loaiDiemDung) }}
                    </div>
                  </div>

                  <div class="stop-lite-row small text-muted">
                    Vật tư: {{ s.chiTietVatTu || '—' }}
                  </div>
                  <div class="stop-lite-row small text-muted">
                    Địa chỉ: {{ s.diaChi || '(Không có địa chỉ chi tiết)' }}
                  </div>
                </div>
              </div>
            </div>

            <div class="claim-row">
              <button class="claim-cancel" @click="closeSheet" :disabled="isBusy">Hủy</button>
              <button class="claim-accept" @click="doClaim" :disabled="isBusy">
                <span v-if="isBusy" class="spinner-border spinner-border-sm mr-2"></span>
                <i v-else class="fa fa-check-circle mr-2"></i>
                Xác nhận nhận đơn
              </button>
            </div>
          </section>
        </div>
      </div>
    </transition>

    <!-- ARRIVE CHECKIN -->
    <transition name="fade-fast">
      <div v-if="arriveModal.show" class="sheet-overlay">
        <div class="sheet-card sheet-card-primary">
          <header class="sheet-header colored">
            <button class="sheet-close colored" @click="closeArrive(true)" :disabled="busyArrive">
              <i class="fa fa-times"></i>
            </button>

            <div class="sheet-title-row">
              <div class="sheet-title-main">
                <div class="sheet-title-text">Xác minh đã đến điểm giao</div>
                <div class="sheet-id mt-1">#{{ currentOrder?.id || '—' }}</div>
              </div>
            </div>

            <div class="arrive-hint">
              Chụp ảnh vị trí khách (cổng, bảng hiệu...). Ảnh dùng để chứng minh tài xế đã tới nơi.
            </div>
          </header>

          <section class="sheet-body">
            <div class="camera-block">
              <div class="camera-label">
                Ảnh xác minh vị trí <span class="text-danger">*</span>
              </div>

              <div class="camera-row">
                <CameraCapture
                  v-model="arriveModal.imageUrl"
                  folder-name="driver_checkin_khach"
                  file-name-prefix="den-khach"
                  modal-title="Chụp ảnh vị trí khách"
                  button-text="Chụp ảnh tại điểm giao"
                  button-class="capture-btn"
                  :disabled="busyArrive"
                />

                <div v-if="arriveModal.imageUrl" class="preview-card">
                  <div class="preview-img-wrap">
                    <img :src="arriveModal.imageUrl" class="preview-img" alt="Ảnh tới khách" />
                  </div>
                  <button
                    type="button"
                    class="preview-del"
                    @click="arriveModal.imageUrl=''"
                    :disabled="busyArrive"
                  >
                    <i class="fa fa-trash mr-1"></i> Xóa ảnh
                  </button>
                </div>
              </div>
            </div>

            <div class="arrive-row">
              <button
                class="arrive-cancel"
                @click="closeArrive(false)"
                :disabled="busyArrive"
              >
                Hủy
              </button>

              <button
                class="arrive-ok"
                :disabled="busyArrive || !arriveModal.imageUrl"
                @click="confirmArrive"
              >
                <span v-if="busyArrive" class="spinner-border spinner-border-sm mr-2"></span>
                <i v-else class="fa fa-check-circle mr-2"></i>
                XÁC NHẬN ĐÃ ĐẾN
              </button>
            </div>
          </section>
        </div>
      </div>
    </transition>
  </div>
</template>



<script>
import { defineComponent } from "vue";
import { mapState, mapActions, mapGetters } from "pinia";
import { useDriverDonHangStore } from "@/stores/driverDonHangStore";
import CameraCapture from "@/components/CameraCapture.vue";

/* util ngày giờ / tiền */
function pad2(n){ return String(n).padStart(2,"0"); }
function parseIsoLocal(s){
  if(!s) return null;
  if(s instanceof Date) return s;
  const m = String(s).match(
    /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/
  );
  if(!m) return null;
  const[,y,M,d,h,mi,se]=m;
  return new Date(+y,+M-1,+d,+h,+mi,+se);
}
function fmtDate(s){
  const d=parseIsoLocal(s); if(!d) return "";
  return `${pad2(d.getDate())}/${pad2(d.getMonth()+1)}/${d.getFullYear()}`;
}
function fmtDateTime(s){
  const d=parseIsoLocal(s); if(!d) return "";
  const datePart=`${pad2(d.getDate())}/${pad2(d.getMonth()+1)}/${d.getFullYear()}`;
  const timePart=`${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
  return `${datePart} ${timePart}`;
}
function money(n){
  if(n==null) return "";
  return (
    Number(n)
      .toLocaleString("vi-VN",{ maximumFractionDigits:0 })
      .replace(/,/g,".") + "đ"
  );
}

export default defineComponent({
  name: "DriverScreen",
  components: { CameraCapture },

  props: {
    taiXe: { type: Object, required: true }
  },

  data(){
    return {
      tab: "CUA_TOI",

      modal: {
        show:false,
        type:null // 'DETAIL' | 'CLAIM'
      },

      arriveModal:{
        show:false,
        stopId:null,
        imageUrl:""
      },

      isBusy:false,
      isRefreshingAll:false,
      arriveSubmitting:false
    };
  },

  computed:{
    ...mapState(useDriverDonHangStore,[
      // danh sách theo từng tab
      "myOrders",
      "poolOrders",
      "completedOrders",

      // loading từng tab
      "isLoadingMyList",
      "isLoadingPool",
      "loading", // <-- loading của fetchCompletedOrders()

      // error từng tab
      "listError",
      "error", // <-- error của fetchCompletedOrders()

      // chi tiết modal
      "currentOrder"
    ]),
    ...mapGetters(useDriverDonHangStore,[
      "myOrdersCount",
      "poolCount"
    ]),

    // loading hiển thị trong khung list chính
    isLoadingList(){
      if(this.tab === "CUA_TOI")   return this.isLoadingMyList;
      if(this.tab === "HANG_DOI")  return this.isLoadingPool;
      if(this.tab === "LICH_SU")   return this.loading;
      return false;
    },

    // error hiển thị trong khung list chính
    activeError(){
      if(this.tab === "CUA_TOI")   return this.listError;
      if(this.tab === "HANG_DOI")  return this.listError;
      if(this.tab === "LICH_SU")   return this.error;
      return null;
    },

    // tổng tiền tăng ca dựa trên completedOrders
    overtimeTotal(){
      if(!this.completedOrders || !this.completedOrders.length) return 0;
      return this.completedOrders.reduce((sum,o)=>{
        const val = o.troCapGiaoGop || 0;
        return sum + (typeof val === "number" ? val : 0);
      },0);
    },
    overtimeTotalDisplay(){
      return money(this.overtimeTotal) || "0đ";
    },

    busyArrive(){
      return this.arriveSubmitting;
    }
  },

  methods:{
    ...mapActions(useDriverDonHangStore,[
      // tab "CỦA TÔI"
      "fetchMyOrders",

      // tab "HÀNG ĐỢI"
      "fetchPool",

      // tab "LỊCH SỬ"
      "fetchCompletedOrders",

      // chi tiết / claim
      "openDetailModal",
      "openClaimModal",
      "clearModal",
      "claimOrder",

      // action điểm dừng
      "runStopCheckpoint",
      "driverXacMinhGiaoHang",
      "reportFailure"
    ]),

    truncate(s,len=60){
      if(!s) return "";
      const str = String(s);
      return str.length <= len ? str : str.substring(0,len) + "…";
    },

    chipStatus(st){
      switch(st){
        case "CHO_NHAN": 
          return "chip-gray";
        case "DA_NHAN":
        case "DA_DEN_DIEM_LAY":
        case "DA_LAY_HANG":
        case "DA_DEN_DIEM_GIAO":
        case "DANG_VAN_CHUYEN":
          return "chip-blue";
        case "HOAN_THANH":
          return "chip-green";
        case "THAT_BAI":
          return "chip-red";
        default:
          return "chip-gray";
      }
    },

    humanStatus(st){
      switch(st){
        case "CHO_NHAN": return "Chờ nhận";
        case "DA_NHAN": return "Đang đi lấy";
        case "DA_DEN_DIEM_LAY": return "Đang bốc hàng";
        case "DA_LAY_HANG": return "Đang đi giao";
        case "DA_DEN_DIEM_GIAO": return "Đang dỡ hàng";
        case "DANG_VAN_CHUYEN": return "Đang vận chuyển";
        case "HOAN_THANH": return "Hoàn thành";
        case "THAT_BAI": return "Thất bại";
        default: return st;
      }
    },

    humanLoai(loai){
      return loai === "LAY_HANG" ? "Lấy hàng" : "Giao hàng";
    },

    stopChip(loai){
      switch(loai){
        case "KHO_LAY":     return "stop-kho";
        case "KHACH_HANG":  return "stop-khach";
        case "KHO_TRA":     return "stop-tra";
        default:            return "stop-other";
      }
    },

    stopLoai(loai){
      switch(loai){
        case "KHO_LAY":     return "Kho lấy";
        case "KHACH_HANG":  return "Khách hàng";
        case "KHO_TRA":     return "Kho trả";
        default:            return loai;
      }
    },

    fmtDate,
    fmtDateTime,
    money,

    openMap(addr){
      if(!addr) return;
      const encoded = encodeURIComponent(addr);
      window.open(
        `https://www.google.com/maps/search/?api=1&query=${encoded}`,
        "_blank",
        "noopener,noreferrer"
      );
    },

    // đổi tab -> gọi đúng API cho tab đó
    async setTab(t){
      this.tab = t;
      if(t === "CUA_TOI"){
        await this.fetchMyOrders();
      } else if(t === "HANG_DOI"){
        await this.fetchPool();
      } else if(t === "LICH_SU"){
        await this.fetchCompletedOrders();
      }
    },

    // nút reload tổng quát
    async refreshAll(){
      this.isRefreshingAll = true;
      try{
        if(this.tab === "CUA_TOI"){
          await this.fetchMyOrders();
        } else if(this.tab === "HANG_DOI"){
          await this.fetchPool();
        } else if(this.tab === "LICH_SU"){
          await this.fetchCompletedOrders();
        }
      } finally {
        this.isRefreshingAll = false;
      }
    },

    // mở chi tiết
    async openDetail(id){
      this.modal.type = "DETAIL";
      this.modal.show = true;
      this.isBusy = true;
      try{
        await this.openDetailModal(id);
      } finally {
        this.isBusy = false;
      }
      document.body.style.overflow = "hidden";
    },

    // mở claim
    async openClaim(o){
      this.modal.type = "CLAIM";
      this.modal.show = true;
      this.isBusy = true;
      try{
        await this.openClaimModal(o);
      } finally {
        this.isBusy = false;
      }
      document.body.style.overflow = "hidden";
    },

    closeSheet(){
      this.modal.show = false;
      this.modal.type = null;
      this.clearModal();
      document.body.style.overflow = "";
    },

    // nhận đơn
    async doClaim(){
      if(!this.currentOrder) return;
      this.isBusy = true;
      try{
        await this.claimOrder(this.currentOrder.id);
        this.closeSheet();
        await this.fetchMyOrders();
        this.tab = "CUA_TOI";
      } finally {
        this.isBusy = false;
      }
    },

    // mở modal chụp ảnh
    openArrive(stop){
      this.arriveModal.show = true;
      this.arriveModal.stopId = stop.id;
      this.arriveModal.imageUrl = "";
      this.arriveSubmitting = false;
      document.body.style.overflow = "hidden";
    },

    closeArrive(force){
      if(!force && this.arriveSubmitting) return;
      this.arriveModal.show = false;
      this.arriveModal.stopId = null;
      this.arriveModal.imageUrl = "";
      document.body.style.overflow = "";
    },

    // xác minh đã đến KH (upload hình)
    async confirmArrive(){
      if(!this.arriveModal.stopId || !this.arriveModal.imageUrl) return;
      this.arriveSubmitting = true;
      this.isBusy = true;
      let updatedOrder=null;
      let ok=false;

      try{
        updatedOrder = await this.driverXacMinhGiaoHang(
          this.arriveModal.stopId,
          this.taiXe.idNguoiDung,
          this.arriveModal.imageUrl
        );
        ok=true;
        this.patchCurrentOrder(updatedOrder);
      }catch(e){
        console.error("confirmArrive error", e);
      }finally{
        this.arriveSubmitting=false;
        this.isBusy=false;

        this.closeArrive(true);
        this.closeSheet();

        // reload list theo tab hiện tại
        if(this.tab==="CUA_TOI"){
          await this.fetchMyOrders();
        }else if(this.tab==="HANG_DOI"){
          await this.fetchPool();
        }else if(this.tab==="LICH_SU"){
          await this.fetchCompletedOrders();
        }

        if(ok && updatedOrder){
          if(updatedOrder.trangThai==="HOAN_THANH"){
            this.$toast?.success?.("Đơn hàng đã hoàn thành 🎉");
          }else{
            this.$toast?.success?.("Đã xác minh đã đến khách 📸");
          }
        }else{
          this.$toast?.error?.("Lỗi khi xác minh giao hàng");
        }
      }
    },

    patchCurrentOrder(newData){
      if(!newData || !this.currentOrder) return;
      if(newData.id !== this.currentOrder.id) return;
      Object.assign(this.currentOrder, newData);
    },

    // đánh dấu điểm dừng (đến / hoàn thành)
    async checkpoint(type, stopId){
      if(!stopId) return;
      this.isBusy = true;
      try{
        await this.runStopCheckpoint(
          type,
          stopId,
          type==="den" ? "Đã đến" : "Hoàn thành"
        );

        // nếu đơn đã hoàn thành -> đóng sheet và reload tab hiện tại
        if(this.currentOrder?.trangThai==="HOAN_THANH"){
          this.closeSheet();
          if(this.tab==="CUA_TOI"){
            await this.fetchMyOrders();
          }else if(this.tab==="HANG_DOI"){
            await this.fetchPool();
          }else if(this.tab==="LICH_SU"){
            await this.fetchCompletedOrders();
          }
        }
      } finally {
        this.isBusy = false;
      }
    },

    // báo thất bại
    async markFailed(){
      if(!this.currentOrder) return;
      const lyDo = prompt("Nhập lý do thất bại (bắt buộc):");
      if(!lyDo || !lyDo.trim()){
        alert("Bạn phải nhập lý do.");
        return;
      }
      this.isBusy = true;
      try{
        await this.reportFailure(this.currentOrder.id, lyDo);
        this.closeSheet();
        if(this.tab==="CUA_TOI"){
          await this.fetchMyOrders();
        }else if(this.tab==="HANG_DOI"){
          await this.fetchPool();
        }else if(this.tab==="LICH_SU"){
          await this.fetchCompletedOrders();
        }
      } finally {
        this.isBusy = false;
      }
    },

    // logic button trong sheet
    isNextStop(stop){
      if(!this.currentOrder || !this.currentOrder.diemDung) return false;
      const arr=[...this.currentOrder.diemDung].sort((a,b)=>a.thuTu-b.thuTu);
      const idx=arr.findIndex(s=>s.id===stop.id);
      if(idx===-1) return false;
      if(idx===0) return true;
      for(let i=0;i<idx;i++){
        const prev=arr[i];
        if(prev.trangThai!=="HOAN_THANH" && prev.trangThai!=="KHO_DA_XAC_MINH"){
          return false;
        }
      }
      return true;
    },
    canMarkArrivedKho(stop){
      return this.isNextStop(stop)
        && stop.loaiDiemDung==="KHO_LAY"
        && (stop.trangThai==="CHO_DEN" || stop.trangThai==="DA_SOAN_HANG");
    },
    canMarkArrivedKhach(stop){
      return this.isNextStop(stop)
        && stop.loaiDiemDung==="KHACH_HANG"
        && stop.trangThai==="CHO_DEN";
    },
    canCheckinKhach(stop){
      return this.isNextStop(stop)
        && stop.loaiDiemDung==="KHACH_HANG"
        && stop.trangThai==="DA_DEN";
    },
    canHoanThanhKhach(stop){
      return this.isNextStop(stop)
        && stop.loaiDiemDung==="KHACH_HANG"
        && stop.trangThai==="DA_DEN";
    },
    isChoKhoXacMinh(stop){
      return stop.trangThai==="DA_DEN" && stop.loaiDiemDung==="KHO_LAY";
    },
    isStopDone(stop){
      return (
        stop.trangThai==="HOAN_THANH" ||
        stop.trangThai==="KHO_DA_XAC_MINH"
      );
    }
  },

  mounted(){
    // khi mở màn hình lần đầu -> load tab default
    this.fetchMyOrders();
  }
});
</script>





<style scoped>
.driver-wrapper{
  background:#f5f6f7;
  padding:0.75rem;
  /* min-height:100vh; */
  font-size:16px;
  line-height:1.4;
  -webkit-tap-highlight-color:rgba(0,0,0,0);
  font-family:system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,sans-serif;
}

/* HEADER */
.driver-header{
  background:#fff;
  border-radius:1rem;
  border:1px solid rgba(0,0,0,0.05);
  padding:0.9rem 1rem;
  margin-bottom:0.75rem;
  display:flex;
  align-items:flex-start;
  justify-content:space-between;
  flex-wrap:wrap;
}
.driver-header-left .hello{
  color:#6c757d;
  font-size:.8rem;
  font-weight:500;
  line-height:1.2;
}
.driver-header-left .name{
  color:#1f2d3d;
  font-size:1rem;
  font-weight:600;
  line-height:1.2;
  max-width:9rem;
}
.driver-header-left .sub{
  color:#6c757d;
  font-size:.75rem;
  line-height:1.2;
  margin-top:.25rem;
}
.driver-header-mid{
  display:flex;
  align-items:flex-start;
}
.stat-box{
  text-align:center;
  padding:0 .5rem;
  min-width:3.5rem;
}
.stat-box .num{
  font-size:1rem;
  font-weight:700;
  color:#1f2d3d;
  line-height:1.1;
}
.stat-box .label{
  font-size:.7rem;
  color:#6c757d;
  font-weight:500;
  line-height:1.2;
}
.refresh-btn{
  background:#f1f3f5;
  border:0;
  width:2rem;
  height:2rem;
  border-radius:.5rem;
  display:flex;
  align-items:center;
  justify-content:center;
  color:#495057;
  font-size:.9rem;
  font-weight:600;
}
.fa-spin{
  animation:fa-spin 1s linear infinite;
}
@keyframes fa-spin{
  0%{transform:rotate(0deg);}
  100%{transform:rotate(359deg);}
}

/* TABS */
.driver-tabs{
  background:#fff;
  border-radius:1rem;
  border:1px solid rgba(0,0,0,0.05);
  margin-bottom:.75rem;
  display:flex;
  overflow:hidden;
}
.driver-tab{
  flex:1;
  min-width:0;
  text-align:left;
  border:0;
  background:transparent;
  padding:.75rem .9rem;
  border-bottom:3px solid transparent;
}
.driver-tab.active{
  background:#eef5ff;
  border-bottom-color:#0d6efd;
}
.driver-tab .tab-main{
  font-size:.9rem;
  font-weight:600;
  color:#1f2d3d;
  line-height:1.2;
  display:flex;
  align-items:center;
}
.driver-tab.active .tab-main{
  color:#0d6efd;
}
.driver-tab .tab-sub{
  margin-top:.25rem;
  font-size:.7rem;
  line-height:1.2;
  display:flex;
  flex-wrap:wrap;
  align-items:center;
}
.pill{
  display:inline-block;
  padding:.2rem .4rem;
  font-size:.7rem;
  font-weight:600;
  line-height:1.2;
  border-radius:.4rem;
  border:1px solid transparent;
}
.pill-primary{
  background:#0d6efd;
  border-color:#0d6efd;
  color:#fff;
}
.pill-success{
  background:#198754;
  border-color:#198754;
  color:#fff;
}
.muted{
  color:#6c757d;
  font-weight:500;
}

/* BODY WRAPPER */
.driver-body{
  background:#fff;
  border-radius:1rem;
  border:1px solid rgba(0,0,0,0.05);
  padding:.75rem;
  min-height:12rem;
}

/* STATE BLOCKS */
.state-block{
  text-align:center;
  font-weight:500;
  color:#6c757d;
  font-size:.9rem;
}
.state-block.error{
  background:#fff5f5;
  border-radius:.75rem;
  padding:.75rem;
  border:1px solid rgba(220,53,69,.3);
  color:#dc3545;
  display:flex;
  justify-content:center;
  align-items:center;
  flex-wrap:wrap;
  line-height:1.3;
}
.state-block.empty .big-icon{
  font-size:2rem;
  line-height:1;
  margin-bottom:.3rem;
}
.state-block.empty .text-main{
  font-size:.9rem;
  font-weight:500;
  color:#495057;
}
.state-block.empty .text-sub{
  font-size:.8rem;
  font-weight:400;
  color:#868e96;
  margin-top:.25rem;
}
.spinner-lg{
  width:2.5rem;
  height:2.5rem;
  border-width:.25rem;
}

/* ORDER LIST */
.order-list{
  display:flex;
  flex-direction:column;
}
.order-item{
  background:#fff;
  border:1px solid rgba(0,0,0,.06);
  box-shadow:0 1px 2px rgba(0,0,0,.03);
  border-radius:.75rem;
  padding:.75rem .75rem .6rem;
  margin-bottom:.6rem;
}
.order-head{
  display:flex;
  justify-content:space-between;
  align-items:flex-start;
  margin-bottom:.5rem;
}
.order-head-left{
  max-width:70%;
}
.order-title{
  font-size:.95rem;
  font-weight:600;
  line-height:1.3;
  color:#0d6efd;
  max-width:14rem;
  word-break:break-word;
}
.text-primary{color:#0d6efd;}
.text-success{color:#198754;}
.order-badges{
  display:flex;
  flex-wrap:wrap;
  margin-top:.4rem;
}
.order-head-right{
  text-align:right;
}
.order-head-right .lbl{
  font-size:.65rem;
  line-height:1.2;
}
.order-head-right .val{
  font-size:.8rem;
  font-weight:600;
  color:#1f2d3d;
  line-height:1.2;
  margin-top:.25rem;
  word-break:break-word;
}
.order-foot{
  font-size:.8rem;
  line-height:1.2;
  font-weight:500;
  color:#495057;
  display:flex;
  align-items:center;
  flex-wrap:wrap;
}

/* HISTORY LIST */
.history-wrapper{
  display:flex;
  flex-direction:column;
}
.history-total{
  background:#f8f9fa;
  border:1px solid rgba(0,0,0,.05);
  border-radius:.75rem;
  padding:.6rem .75rem;
  margin-bottom:.75rem;
  display:flex;
  justify-content:space-between;
  align-items:flex-start;
  flex-wrap:wrap;
  font-size:.8rem;
  line-height:1.3;
  font-weight:500;
  color:#1f2d3d;
}
.history-total-left .total-label{
  color:#6c757d;
  font-size:.8rem;
  font-weight:500;
}
.history-total-left .total-val{
  font-size:.9rem;
  font-weight:700;
  color:#1f2d3d;
  margin-top:.25rem;
}
.history-total-right{
  font-size:.8rem;
  font-weight:600;
  color:#495057;
}
.history-item{
  background:#fff;
  border:1px solid rgba(0,0,0,.06);
  box-shadow:0 1px 2px rgba(0,0,0,.03);
  border-radius:.75rem;
  padding:.75rem;
  margin-bottom:.6rem;
  font-size:.8rem;
  line-height:1.3;
}
.history-head{
  display:flex;
  justify-content:space-between;
  align-items:flex-start;
  flex-wrap:nowrap;
  margin-bottom:.5rem;
}
.history-head-left{
  min-width:0;
  max-width:70%;
}
.history-title{
  font-size:.9rem;
  font-weight:600;
  line-height:1.3;
  color:#1f2d3d;
  word-break:break-word;
}
.history-badges{
  margin-top:.4rem;
  display:flex;
  flex-wrap:wrap;
  align-items:center;
}
.history-head-right{
  max-width:30%;
  text-align:right;
  font-size:.75rem;
  line-height:1.3;
  font-weight:600;
  color:#dc3545;
  word-break:break-word;
}
.urgent-flag{
  color:#dc3545;
}
.history-body{
  line-height:1.4;
  word-break:break-word;
}
.history-foot{
  margin-top:.4rem;
  line-height:1.4;
  word-break:break-word;
}

/* CHIPS */
.chip{
  font-size:.7rem;
  line-height:1.2;
  font-weight:600;
  padding:.25rem .4rem;
  border-radius:.4rem;
  margin-right:.4rem;
  margin-bottom:.4rem;
  color:#fff;
  background:#6c757d;
  border:1px solid transparent;
}
.chip-outline{
  background:transparent;
  color:#0d6efd;
  border:1px solid #0d6efd;
}
.chip-ghost{
  background:#f1f3f5;
  border:1px solid #dee2e6;
  color:#495057;
}
.chip-blue{background:#0d6efd;}
.chip-green{background:#198754;}
.chip-red{background:#dc3545;}
.chip-gray{background:#6c757d;}
.chip-urgent{
  background:#e03131;
}
.chip-done{
  background:#198754;
}
.chip-overtime{
  background:#ffd43b;
  color:#000;
  border-color:#ffd43b;
}

/* BOTTOM SHEET OVERLAY */
.sheet-overlay{
  position:fixed;
  inset:0;
  background:rgba(0,0,0,.5);
  z-index:9999;
  display:flex;
  justify-content:center;
  align-items:flex-end;
  padding:.5rem;
}
.sheet-card{
  background:#fff;
  width:100%;
  max-width:480px;
  max-height:90vh;
  border-radius:1rem;
  display:flex;
  flex-direction:column;
  overflow:hidden;
  box-shadow:0 20px 40px rgba(0,0,0,.3);
  animation:sheet-up .2s ease-out;
}
.sheet-card-success{
  background:#198754;
  color:#fff;
}
.sheet-card-primary{
  background:#0d6efd;
  color:#fff;
}
@keyframes sheet-up{
  0%{transform:translateY(20px);opacity:0;}
  100%{transform:translateY(0);opacity:1;}
}

/* SHEET HEADER */
.sheet-header{
  background:#fff;
  border-bottom:1px solid rgba(0,0,0,.05);
  padding:.75rem 1rem .5rem;
  position:relative;
}
.sheet-header.colored{
  background:transparent;
  border-bottom:1px solid rgba(255,255,255,.3);
  color:#fff;
}
.sheet-close{
  position:absolute;
  top:.75rem;
  right:1rem;
  background:rgba(0,0,0,.05);
  color:#495057;
  border:0;
  width:2rem;
  height:2rem;
  border-radius:.5rem;
  display:flex;
  align-items:center;
  justify-content:center;
  font-size:.9rem;
}
.sheet-close.colored{
  background:rgba(255,255,255,.15);
  border:1px solid rgba(255,255,255,.3);
  color:#fff;
}
.sheet-title-row{
  display:flex;
  justify-content:space-between;
  align-items:flex-start;
  flex-wrap:nowrap;
}
.sheet-title-main{
  flex:1 1 auto;
  min-width:0;
  padding-right:.75rem;
}
.sheet-title-text{
  font-size:1rem;
  font-weight:600;
  line-height:1.3;
  color:#1f2d3d;
  word-break:break-word;
}
.sheet-header.colored .sheet-title-text{
  color:#fff;
}
.sheet-title-badges{
  display:flex;
  flex-wrap:wrap;
  align-items:center;
  margin-top:.5rem;
  font-size:.75rem;
  line-height:1.3;
  font-weight:500;
}
.sheet-header.colored .sheet-title-badges .chip,
.sheet-header.colored .sheet-title-badges .chip-outline{
  background:rgba(0,0,0,.2);
  border:1px solid rgba(255,255,255,.4);
  color:#fff;
}
.sheet-id{
  font-size:.7rem;
  font-weight:500;
  line-height:1.2;
  color:#6c757d;
}
.sheet-header.colored .sheet-id{
  color:rgba(255,255,255,.9);
}
.sheet-title-side{
  text-align:right;
  min-width:5.5rem;
  font-size:.8rem;
  line-height:1.3;
  font-weight:600;
  color:#1f2d3d;
}
.sheet-header.colored .sheet-title-side{
  color:#fff;
}
.sheet-claim-name{
  font-size:.9rem;
  font-weight:600;
  line-height:1.3;
  color:rgba(255,255,255,.9);
  margin-top:.25rem;
  word-break:break-word;
}

/* MINI INFO */
.mini-info{
  background:#f8f9fa;
  border:1px solid rgba(0,0,0,.05);
  border-radius:.75rem;
  padding:.6rem .75rem;
  margin-top:.75rem;
  font-size:.8rem;
  line-height:1.3;
  font-weight:500;
  color:#1f2d3d;
}
.sheet-header.colored .mini-info{
  background:rgba(255,255,255,.15);
  border:1px solid rgba(255,255,255,.4);
  color:#fff;
}
.mini-row{
  display:flex;
  align-items:flex-start;
  margin-bottom:.5rem;
}
.mini-row:last-child{margin-bottom:0;}
.mini-icon{
  width:28px;
  height:28px;
  border-radius:.5rem;
  background:#eef5ff;
  color:#0d6efd;
  display:flex;
  align-items:center;
  justify-content:center;
  font-size:.8rem;
  margin-right:.5rem;
}
.sheet-header.colored .mini-icon{
  background:rgba(0,0,0,.2);
  color:#fff;
}
.mini-main{flex:1 1 auto;min-width:0;}
.mini-label{
  color:#6c757d;
  font-size:.7rem;
  line-height:1.2;
  font-weight:500;
}
.sheet-header.colored .mini-label{
  color:rgba(255,255,255,.75);
}
.mini-value{
  font-size:.8rem;
  line-height:1.3;
  font-weight:600;
  color:#1f2d3d;
  word-break:break-word;
}
.sheet-header.colored .mini-value{
  color:#fff;
}
.call-inline{
  background:#e7f5ff;
  border:1px solid #74c0fc;
  border-radius:.5rem;
  padding:.3rem .5rem;
  font-size:.8rem;
  font-weight:600;
  line-height:1.2;
  color:#1c7ed6;
  text-decoration:none;
  display:inline-flex;
  align-items:center;
}
.call-inline.disabled{
  opacity:.4;
  pointer-events:none;
}
.sheet-header.colored .call-inline{
  background:rgba(0,0,0,.2);
  border:1px solid rgba(255,255,255,.4);
  color:#fff;
}
.map-inline{
  background:transparent;
  border:0;
  padding:0;
  font-size:.8rem;
  font-weight:600;
  line-height:1.3;
  color:#0d6efd;
  text-align:left;
  text-decoration:underline;
}
.sheet-header.colored .map-inline{
  color:#fff;
  text-decoration:underline;
}

/* NOTES */
.notes-wrap{
  margin-top:.75rem;
  display:grid;
  grid-template-columns:1fr 1fr;
  grid-gap:.5rem;
}
.note-block{
  border-radius:.75rem;
  border:1px solid rgba(0,0,0,.05);
  font-size:.75rem;
  line-height:1.3;
  font-weight:500;
  padding:.6rem;
}
.note-sales{
  background:#e7f5ff;
  border-color:#a5d8ff;
  color:#1c7ed6;
}
.note-warehouse{
  background:#fff4e6;
  border-color:#ffd8a8;
  color:#e67700;
}
.note-head{
  font-weight:600;
  display:flex;
  align-items:center;
  font-size:.75rem;
  margin-bottom:.25rem;
}
.note-body{
  word-break:break-word;
}

/* SHEET BODY SCROLL */
.sheet-body{
  flex:1 1 auto;
  overflow-y:auto;
  -webkit-overflow-scrolling:touch;
  background:#fff;
  padding:.75rem 1rem 1rem;
  font-size:.8rem;
  line-height:1.3;
  color:#1f2d3d;
  font-weight:500;
}
.sheet-card-success .sheet-body,
.sheet-card-primary .sheet-body{
  background:transparent;
  color:#fff;
}

/* ROUTE / STOPS */
.section-head{
  font-size:.9rem;
  font-weight:600;
  color:#1f2d3d;
  line-height:1.3;
  display:flex;
  flex-wrap:wrap;
  align-items:baseline;
  margin-bottom:.5rem;
}
.section-head .count{
  font-size:.8rem;
  font-weight:500;
  margin-left:.4rem;
  color:#6c757d;
}
.stop-list{
  display:flex;
  flex-direction:column;
  margin-bottom:1rem;
}
.stop-card{
  background:#fff;
  border:1px solid rgba(0,0,0,.06);
  box-shadow:0 1px 2px rgba(0,0,0,.03);
  border-radius:.75rem;
  padding:.75rem;
  margin-bottom:.6rem;
  display:flex;
  justify-content:space-between;
  align-items:flex-start;
  flex-wrap:nowrap;
}
.stop-card.done{
  opacity:.7;
}
.stop-left{
  flex:1 1 auto;
  min-width:0;
  padding-right:.5rem;
  font-size:.8rem;
  line-height:1.4;
  font-weight:500;
  color:#1f2d3d;
}
.stop-top{
  display:flex;
  flex-wrap:wrap;
  align-items:center;
  font-size:.8rem;
  font-weight:600;
  line-height:1.3;
  color:#1f2d3d;
}
.idx{
  font-weight:700;
  margin-right:.4rem;
}
.stop-top .title{
  font-weight:600;
  max-width:11rem;
  word-break:break-word;
}
.stop-chip{
  font-size:.65rem;
  line-height:1.2;
  font-weight:600;
  padding:.2rem .4rem;
  border-radius:.4rem;
  color:#fff;
  margin-left:.5rem;
  margin-top:.4rem;
}
.stop-kho{background:#0d6efd;}
.stop-khach{background:#e03131;}
.stop-tra{background:#6c757d;}
.stop-other{background:#495057;}
.stop-addr{
  margin-top:.4rem;
  font-size:.75rem;
  line-height:1.3;
  font-weight:500;
  word-break:break-word;
}
.map-inline-link{
  border:0;
  background:transparent;
  padding:0;
  font-size:.75rem;
  font-weight:600;
  line-height:1.3;
  color:#0d6efd;
  text-decoration:underline;
  text-align:left;
  word-break:break-word;
}
.stop-mat{
  margin-top:.4rem;
}
.stop-time{
  margin-top:.4rem;
  color:#6c757d;
  font-size:.7rem;
  line-height:1.3;
  font-weight:500;
}
.stop-right{
  flex:0 0 auto;
  min-width:7rem;
  max-width:8rem;
  text-align:right;
  display:flex;
  flex-direction:column;
  align-items:flex-end;
}
.act-btn{
  width:100%;
  border:0;
  border-radius:.6rem;
  padding:.5rem .6rem;
  min-height:2.25rem;
  text-align:center;
  font-size:.75rem;
  line-height:1.2;
  font-weight:600;
  color:#fff;
  margin-bottom:.4rem;
  display:flex;
  align-items:center;
  justify-content:center;
  box-shadow:0 2px 4px rgba(0,0,0,.15);
}
.act-primary{background:#0d6efd;}
.act-warn{background:#f08c00;}
.act-ok{background:#198754;}
.pending-block{
  font-size:.7rem;
  font-weight:600;
  color:#f08c00;
  background:#fff4e6;
  border:1px solid #ffd8a8;
  border-radius:.6rem;
  padding:.4rem .5rem;
  line-height:1.2;
  text-align:center;
  margin-bottom:.4rem;
  width:100%;
}
.done-ic{
  font-size:1.4rem;
  line-height:1;
  color:#198754;
}

/* PXK */
.pxk-block{
  border-top:1px solid rgba(0,0,0,.05);
  padding-top:.75rem;
  margin-bottom:1rem;
}
.pxk-label{
  font-size:.8rem;
  font-weight:600;
  line-height:1.3;
  color:#1f2d3d;
}
.pxk-link{
  font-size:.8rem;
  font-weight:600;
  line-height:1.3;
  color:#0d6efd;
  text-decoration:underline;
}
.pxk-inline{
  font-size:.8rem;
  font-weight:600;
  color:#fff;
  text-decoration:underline;
}

/* FAIL */
.fail-wrap{
  border-top:1px solid rgba(0,0,0,.05);
  padding-top:1rem;
}
.fail-btn{
  width:100%;
  border:1px solid rgba(220,53,69,.3);
  border-radius:.75rem;
  background:#fff5f5;
  color:#dc3545;
  font-size:.8rem;
  line-height:1.3;
  font-weight:600;
  padding:.6rem .75rem;
  display:flex;
  align-items:center;
  justify-content:center;
}

/* CLAIM BODY */
.stop-list-lite{
  display:flex;
  flex-direction:column;
}
.stop-lite{
  border-radius:.75rem;
  border:1px solid rgba(255,255,255,.4);
  background:rgba(255,255,255,.1);
  padding:.75rem;
  margin-bottom:.6rem;
  font-size:.8rem;
  line-height:1.3;
  box-shadow:0 1px 2px rgba(0,0,0,.03);
}
.stop-lite-left{color:#fff;}
.stop-lite-top{
  display:flex;
  flex-wrap:wrap;
  align-items:center;
  font-size:.8rem;
  font-weight:600;
  line-height:1.3;
}
.stop-lite-top .title{
  font-weight:600;
  max-width:11rem;
  margin-left:.4rem;
  word-break:break-word;
}
.stop-lite-row{
  margin-top:.4rem;
  color:rgba(255,255,255,.9) !important;
}
.claim-row{
  border-top:1px solid rgba(255,255,255,.3);
  margin-top:1rem;
  padding-top:1rem;
  display:flex;
  justify-content:space-between;
  flex-wrap:wrap;
}
.claim-cancel{
  flex:1 1 auto;
  min-width:40%;
  background:rgba(255,255,255,.15);
  border:1px solid rgba(255,255,255,.3);
  border-radius:.75rem;
  color:#fff;
  font-size:.8rem;
  font-weight:600;
  padding:.6rem .75rem;
  text-align:center;
}
.claim-accept{
  flex:1 1 auto;
  min-width:55%;
  background:#198754;
  color:#fff;
  border:0;
  border-radius:.75rem;
  font-size:.8rem;
  font-weight:600;
  padding:.6rem .75rem;
  text-align:center;
  display:flex;
  align-items:center;
  justify-content:center;
  box-shadow:0 4px 8px rgba(0,0,0,.3);
}

/* ARRIVE MODAL BODY */
.arrive-hint{
  color:rgba(255,255,255,.8);
  font-size:.8rem;
  line-height:1.3;
  font-weight:500;
  padding-top:.5rem;
}
.camera-block{
  margin-bottom:1.5rem;
}
.camera-label{
  font-size:.8rem;
  font-weight:600;
  line-height:1.3;
  color:#1f2d3d;
  margin-bottom:.5rem;
}
.sheet-card-primary .camera-label{
  color:#fff;
}
.camera-row{
  display:flex;
  flex-wrap:wrap;
  align-items:flex-start;
}
.capture-btn{
  background:#fff;
  border:2px dashed #6dfd0d !important;
  border-radius:.75rem;
  padding:.75rem;
  min-width:8rem;
  font-size:.8rem;
  font-weight:600;
  line-height:1.3;
  color:#1f2d3d !important;
  text-align:center;
  display:flex;
  align-items:center;
  justify-content:center;
}

.sheet-card-primary .capture-btn{
  border-color:#fff;
  color:#fff !important;
  background:rgb(255, 255, 255) !important;
}
.preview-card{
  background:#fff;
  border-radius:.75rem;
  border:1px solid rgba(0,0,0,.05);
  box-shadow:0 1px 2px rgba(0,0,0,.05);
  padding:.5rem;
  font-size:.8rem;
  max-width:7rem;
  margin-left:.75rem;
}
.preview-img-wrap{
  border-radius:.5rem;
  border:1px solid rgba(0,0,0,.07);
  overflow:hidden;
  margin-bottom:.5rem;
}
.preview-img{
  width:100%;
  height:auto;
  display:block;
}
.preview-del{
  width:100%;
  border:0;
  border-radius:.5rem;
  background:#dc3545;
  color:#fff;
  font-size:.7rem;
  font-weight:600;
  line-height:1.2;
  padding:.4rem .5rem;
  text-align:center;
}
.arrive-row{
  display:flex;
  flex-wrap:wrap;
  justify-content:space-between;
  align-items:flex-start;
}
.arrive-cancel{
  flex:1 1 auto;
  min-width:40%;
  background:#fff;
  border:1px solid #dee2e6;
  border-radius:.75rem;
  font-size:.8rem;
  font-weight:600;
  line-height:1.3;
  padding:.6rem .75rem;
  text-align:center;
  color:#495057;
}
.arrive-ok{
  flex:1 1 auto;
  min-width:55%;
  background:#0d6efd;
  border:0;
  border-radius:.75rem;
  font-size:.8rem;
  font-weight:600;
  line-height:1.3;
  padding:.6rem .75rem;
  text-align:center;
  color:#fff;
  display:flex;
  align-items:center;
  justify-content:center;
  box-shadow:0 4px 8px rgba(13,110,253,.4);
}

/* TRANSITIONS */
.fade-fast-enter-active,
.fade-fast-leave-active{
  transition:opacity .18s ease;
}
.fade-fast-enter-from,
.fade-fast-leave-to{
  opacity:0;
}

@media(min-width:480px){
  .driver-header,
  .driver-tabs,
  .driver-body{
    margin-left:auto;
    margin-right:auto;
    max-width:100%;
  }
  .sheet-overlay{
    align-items:center;
  }
  .sheet-card{
    max-height:85vh;
  }
}
</style>
